import {zodResolver} from '@hookform/resolvers/zod';
import {useMutation} from '@tanstack/react-query';
import {useForm} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';

import CheckMark from '@/assets/icons/check-mark.svg?react';
import ErrorOutlined from '@/assets/icons/error-outlined.svg?react';
import TextInput from '@/components_legacy/common/TextInput/TextInput';
import {sendContactUsForm} from '@/entities/app/api/sendContactUsForm';
import {
  ContactFormValues,
  contactSchema,
} from '@/entities/app/validations/contactUsForm';
import Button from '@/shared/components/button';
import DialogModal from '@/shared/components/modals/base/DialogModal';
import Textarea from '@/shared/components/TextArea';
import {useToggle} from '@/shared/hooks/useToggle';

import ContactUsLabel from './components/ContactUsLabel';

const initialValues: ContactFormValues = {
  name: '',
  email: '',
  message: '',
};

const inputIds: Record<keyof ContactFormValues, string> = {
  name: 'contacts-name',
  email: 'contacts-email',
  message: 'contacts-message',
};

const ContactUsForm = () => {
  const successModal = useToggle();
  const errorModal = useToggle();
  const mutation = useMutation({
    mutationFn: sendContactUsForm,
    onSuccess: () => {
      successModal.show();
    },
    onError: (error) => {
      errorModal.show(error.message);
    },
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: {errors, isSubmitting, submitCount},
    watch,
  } = useForm<ContactFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(contactSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
  });
  const emailValue = watch('email');
  const disabled = isSubmitting || mutation.isPending;
  const showEmailCheck = emailValue && !errors.email && submitCount > 0;

  const onSubmit = handleSubmit(async (formValues) => {
    await mutation.mutateAsync(formValues);
    reset(initialValues);
  });

  return (
    <div className="mx-auto flex w-full max-w-[662px] flex-col gap-8 px-4">
      <div className="flex flex-col gap-2">
        <h4 className="font-semibold text-lg text-link sm:text-xl">
          Help / Support
        </h4>
        <p className="text-black text-sm sm:text-base">
          We're here to help. Send us a message.
        </p>
      </div>
      <form onSubmit={onSubmit} className="mx-auto flex w-full flex-col gap-12">
        <div className="flex flex-col gap-5 sm:gap-8">
          <div className="flex flex-col gap-1">
            <ContactUsLabel htmlFor={inputIds.name} label="Name*" />
            <div className="relative">
              <TextInput
                {...register('name')}
                id={inputIds.name}
                disabled={disabled}
                placeholder="Your name"
                className={twMerge(
                  'h-[50px] rounded-lg border border-inactive bg-white px-3.5 py-3 text-black text-lg focus:border-link sm:h-[60px] sm:text-xl',
                  errors.name && 'border-error pr-11 focus:border-error'
                )}
              />
              {errors.name && (
                <ErrorOutlined className="absolute top-1/2 right-3 size-6 shrink-0 -translate-y-1/2" />
              )}
            </div>
            <p className="text-error text-xs empty:hidden">
              {errors.name?.message}
            </p>
          </div>
          <div className="relative flex flex-col gap-1">
            <ContactUsLabel htmlFor={inputIds.email} label="E-mail*" />
            <div className="relative">
              <TextInput
                {...register('email')}
                id={inputIds.email}
                disabled={disabled}
                inputMode="email"
                placeholder="Your email"
                className={twMerge(
                  'h-[50px] rounded-lg border border-inactive bg-white px-3.5 py-3 text-black text-lg focus:border-link sm:h-[60px] sm:text-xl',
                  errors.email && 'border-error pr-11 focus:border-error',
                  showEmailCheck && 'pr-11'
                )}
              />
              {errors.email && (
                <ErrorOutlined className="absolute top-1/2 right-3 size-6 shrink-0 -translate-y-1/2" />
              )}
              {showEmailCheck && (
                <CheckMark className="absolute top-1/2 right-3 size-6 shrink-0 -translate-y-1/2 fill-lime-green" />
              )}
            </div>
            <p className="text-error text-xs empty:hidden">
              {errors.email?.message}
            </p>
          </div>
          <div className="relative flex flex-col gap-1">
            <ContactUsLabel htmlFor={inputIds.message} label="Message*" />
            <Textarea
              {...register('message')}
              id={inputIds.message}
              disabled={disabled}
              placeholder="Your message"
              rows={4}
              className={twMerge(
                'h-[124px] rounded-lg border border-inactive bg-white px-3.5 py-3 text-base text-black focus:border-link sm:text-xl',
                errors.message && 'border-error focus:border-error'
              )}
            />
            <p className="text-error text-xs empty:hidden">
              {errors.message?.message}
            </p>
          </div>
        </div>
        <Button
          type="submit"
          disabled={disabled}
          variant="primary"
          className="mx-auto max-w-[436px]"
        >
          Contact us
        </Button>
      </form>
      {successModal.open && (
        <DialogModal
          onClose={successModal.hide}
          okButton={{label: 'Okay', onClick: successModal.hide}}
        >
          <p className="font-semibold text-lg">Thanks! We’ll get back soon.</p>
        </DialogModal>
      )}
      {errorModal.open && (
        <DialogModal
          onClose={errorModal.hide}
          okButton={{label: 'Okay', onClick: errorModal.hide}}
        >
          <div className="flex flex-col items-center gap-1.5">
            <ErrorOutlined className="size-7 shrink-0" />
            <p className="font-semibold text-lg">{String(errorModal.value)}</p>
          </div>
        </DialogModal>
      )}
    </div>
  );
};

export default ContactUsForm;
